import { Location } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { BehaviorSubject, lastValueFrom } from 'rxjs';

import { LoginService } from '../../../../api';
import { MeDto } from '../../../core/types/MeDto';
import {
  LocalStorageItem,
  clearStorageCache,
  setLocalStorageItem,
} from '../../../core/utils';
import { ROUTER_UTILS } from '../../../core/utils/router.utils';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private router = inject(Router);
  private location = inject(Location);
  private readonly loginService = inject(LoginService);

  isLoggedIn$ = new BehaviorSubject<boolean>(false);

  // Drives the app-wide overlay. Signing in and signing out are both an API
  // round trip followed by a navigation into a lazily loaded route, and both
  // end with the whole shell being replaced. Left uncovered, the click reads as
  // ignored and then the pieces swap one at a time — on sign-out the header
  // goes on the flag flip while the page underneath waits for the chunk.
  private readonly authTransitions = signal(0);
  readonly isAuthPending = computed(() => this.authTransitions() > 0);

  // Counted, not a boolean: a single session expiry can start two overlapping
  // sign-outs (the 401 interceptor and the route guard), and the one that
  // finishes first must not uncover the one still navigating.
  beginAuthTransition(): void {
    this.authTransitions.update((count) => count + 1);
  }

  endAuthTransition(): void {
    this.authTransitions.update((count) => Math.max(0, count - 1));
  }

  get isLoggedIn(): boolean {
    return this.isLoggedIn$.getValue();
  }

  signIn(me: MeDto): void {
    setLocalStorageItem(LocalStorageItem.meInformation, me);
    // Only the opaque user ID, so issues count affected users without PII.
    // /me returns it, but the API schema doesn't declare it yet.
    const { id } = me as MeDto & { id?: string };
    Sentry.setUser(id ? { id } : null);
    this.isLoggedIn$.next(true);
  }

  async signOut(
    returnUrl: string | undefined = undefined,
    apiLogout: boolean,
  ): Promise<void> {
    this.beginAuthTransition();
    try {
      if (apiLogout) {
        try {
          await lastValueFrom(this.loginService.logout());
        } catch (e) {
          console.error(e);
        }
      }
      clearStorageCache();
      Sentry.setUser(null);
      this.isLoggedIn$.next(false);

      const { root, signIn } = ROUTER_UTILS.config.auth;
      const signInPath = `/${root}/${signIn}`;

      // A single session-expiry can trigger signOut more than once (the 401
      // interceptor and the route guard both fire on a bootstrap deep-link).
      // Once we are already on the sign-in page, skip re-navigating so we don't
      // clobber the returnUrl that the first redirect captured.
      if (this.location.path().split('?')[0] === signInPath) {
        return;
      }

      // Never send the user back to an auth page after signing in.
      const safeReturnUrl = returnUrl?.startsWith(signInPath)
        ? undefined
        : returnUrl;

      // Awaited, so the overlay comes down with the sign-in page on screen
      // rather than one frame before it.
      await this.router.navigate(['/', root, signIn], {
        queryParams: { returnUrl: safeReturnUrl },
      });
    } finally {
      this.endAuthTransition();
    }
  }
}
